/**
 * useOrderUpdates Hook - Vendor App
 * 
 * Manages real-time order updates for vendors via WebSocket.
 * Subscribes to order-updates channel and filters events by vendor ID.
 */

import { useEffect, useRef, useState } from 'react';
import { wsClient } from '@/lib/ws';
import { useOrdersStore } from '@/store/orders';
import { useAuthStore } from '@/store/auth';
import { logger } from '@/lib/logger';

export type ConnectionState = 'idle' | 'connecting' | 'connected' | 'disconnected' | 'error';

interface OrderUpdateEvent {
  order_id: string;
  status?: string;
  vendor_id?: string;
  customer_id?: string;
  [key: string]: any;
}

export function useOrderUpdates() {
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const fetchOrders = useOrdersStore((state) => state.fetchOrders);
  const user = useAuthStore((state) => state.user);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const handlerRef = useRef<((data: OrderUpdateEvent) => void) | null>(null);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      setConnectionState('idle');
      return;
    }

    // Check if offline
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setConnectionState('disconnected');
      logger.info('Browser offline, skipping WebSocket connection');
      return;
    }

    // Connect to WebSocket
    setConnectionState('connecting');
    
    try {
      wsClient.connect();
      
      // Define event handler
      const handleOrderUpdate = (data: OrderUpdateEvent) => {
        logger.info('Order update received (vendor)', { orderId: data.order_id, status: data.status });
        
        // Refetch orders when update received
        // (Vendor needs full order details, not just status)
        fetchOrders();
      };

      // Store handler ref for cleanup
      handlerRef.current = handleOrderUpdate;

      // Subscribe to order updates channel
      wsClient.subscribe('order-updates', handleOrderUpdate);
      
      setConnectionState('connected');
      logger.info('Vendor subscribed to order updates');

    } catch (error) {
      logger.error('Failed to connect to WebSocket', error as Error);
      setConnectionState('error');
    }

    // Cleanup on unmount or auth change
    return () => {
      if (handlerRef.current) {
        wsClient.unsubscribe('order-updates', handlerRef.current);
        logger.info('Vendor unsubscribed from order updates');
        handlerRef.current = null;
      }
    };
  }, [isAuthenticated, user?.id, fetchOrders]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => {
      logger.info('Browser back online, reconnecting WebSocket');
      setConnectionState('connecting');
      wsClient.connect();
    };

    const handleOffline = () => {
      logger.info('Browser offline, disconnecting WebSocket');
      setConnectionState('disconnected');
      wsClient.disconnect();
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
  };
}






